<template>
  <BaseDialog
    :title="dsiplayText.title"
    width="610px"
    hideConfirm
    :closeOnClickModal="false"
    @close="onClose"
  >
    <FlexLayout col :gap="20" style="margin-bottom: 40px;">
      <div style="margin-bottom: 12px;">
        <div class="text-sub text-gray-80">{{ dsiplayText.description }}</div>
      </div>

      <FlexLayout col>
        <BobForm
          :formRef.sync="formRef"
          :fields="fields"
          :rules="formRules"
          :model="formData"
        />

        <FlexLayout>
          <BaseElButton type="primary" :loading="loading" @click="onConfirm">
            <FlexLayout align="center" :gap="4" inline>
              AI 生成文案
              <MaterialIcon size="14">auto_awesome</MaterialIcon>
            </FlexLayout>
          </BaseElButton>
          <BaseElButton plain :disabled="!formData.announcementText" @click="copyText">
            <FlexLayout align="center" :gap="4" inline>
              複製文案
              <MaterialIcon size="14">content_copy_outlined</MaterialIcon>
            </FlexLayout>
          </BaseElButton>
        </FlexLayout>
      </FlexLayout>

      <FlexLayout v-if="generated" align="center" :gap="8" class="text-danger">
        <SVGIcon icon="common-warning" fill="var(--danger)" />
        實際客戶預約時間受限於設備空檔、預約開放時間設定，請以實際預約系統為準
      </FlexLayout>
    </FlexLayout>
  </BaseDialog>
</template>

<script>
import { computed, defineComponent, provide, ref } from 'vue'
import dayjs from '@/lib/dayjs'
import BaseDialog from '@/components/Dialog/BaseDialog.vue'
import BobForm from '@/components/BobForm.vue'
import { useBaseForm } from '@/use/useForm'
import SVGIcon from '@/components/SVGIcon.vue'
import { LlmGenerateTimeSlotAnnouncementText } from '@/api/configurationHint'
import { useShop } from '@/use/shop'
import { noEmptyRules } from '@/validation'
import copy from 'clipboard-copy'

export default defineComponent({
  name: 'AIGenerateTimeSlotAnnouncementModal',
  components: {
    BaseDialog,
    BobForm,
    SVGIcon,
  },
  setup (props, { emit }) {
    const { formData, formRef, initFormData, checkForm, loading } = useBaseForm()
    const { shopId } = useShop()
    const generated = ref(false)

    initFormData({
      dateRange: [],
      announcementText: '',
    })

    const formRules = {
      dateRange: [
        noEmptyRules(),
        {
          trigger: 'change',
          validator: (rule, value, callback) => {
            // 額外檢查 dateRange 是否超過 14 天
            if (value && value.length === 2) {
              const start = new Date(value[0])
              const end = new Date(value[1])
              const diffDays = Math.floor((end - start) / (24 * 3600 * 1000))
              if (diffDays > 13) {
                callback(new Error('預約空檔區間最多只能選 14 天'))
              }
            }
            callback()
          },
        },
      ],

    }

    const dsiplayText = computed(() => ({
      title: 'AI 生成預約空檔公告',
      description: '可針對某個日期區間，由 AI 抓取區間內所有預約空檔時段，並生成一則公告文案，請選擇「預約空檔區間」並點擊左下角「AI 生成文案」',
      formItemLabel: {
        dateRange: '預約空檔區間',
        announcementText: '文案',
      },
      placeholder: {
        announcementText: '請選擇「預約空檔區間」並點擊左下角「AI 生成文案」',
      },
      btn: {
        aiGenerate: 'AI 生成文案',
        copyText: '複製文案',
      },
    }))

    const today = dayjs().startOf('day')

    // BobForm fields definition following pattern
    const fields = computed(() => [
      {
        prop: 'dateRange',
        label: dsiplayText.value.formItemLabel.dateRange,
        body: {
          component: 'el-date-picker',
          props: {
            type: 'daterange',
            'start-placeholder': '開始日期',
            'end-placeholder': '結束日期',
            value: formData.dateRange,
            style: { width: '100%' },
            defaultTime: ['00:00:00', '23:59:59'],
            // 限制最多選取 14 天範圍：使用 disabledDate 在 UI 上禁止超出範圍的日期
            pickerOptions: {
              disabledDate (date) {
                // 禁用以過去時間
                if (dayjs(date).isBefore(today)) {
                  return true
                }

                return false
              },
            },
          },
        },
      },
      {
        prop: 'announcementText',
        label: dsiplayText.value.formItemLabel.announcementText,
        body: {
          component: 'BaseElInput',
          props: {
            type: 'textarea',
            autosize: { minRows: 4, maxRows: 8 },
            placeholder: dsiplayText.value.placeholder.announcementText,
            value: formData.announcementText,
            style: { width: '100%' },
          },
        },
      },
    ])

    const bobFormRef = ref(null)

    const onConfirm = async () => {
      if (loading.value) return
      // 先檢查表單基本驗證
      const valid = await checkForm(formRef.value)
      if (!valid) return
      loading.value = true
      const [res, errMsg] = await LlmGenerateTimeSlotAnnouncementText({
        shopId: shopId.value,
        start: dayjs(formData.dateRange[0]).startOf('day').toISOString(),
        end: dayjs(formData.dateRange[1]).endOf('day').toISOString(),
      })
      loading.value = false
      if (errMsg) {
        window.$message.error(errMsg)
        return
      }
      formData.announcementText = res.content
      generated.value = true
      emit('generated')
    }

    const copyText = () => {
      if (!formData.announcementText) return
      try {
        copy(formData.announcementText)
        window.$message.success('複製文案成功')
      } catch (error) {
        console.error('Failed to copy text:', error)
      }
    }

    const onClose = () => {
      emit('close')
    }

    provide('formData', formData)

    return {
      generated,
      copyText,
      formRules,
      dsiplayText,
      fields,
      formData,
      formRef,
      bobFormRef,
      onConfirm,
      onClose,
      loading,
    }
  },
})
</script>

<style lang="postcss" scoped>

</style>
